// 36-Class Beginner Chess Curriculum for Kids
// Each class includes title, category, description, theory guidelines for coach, FEN demonstration setup, and interactive puzzles.

const BASICS_CURRICULUM = [
  {
    id: 1,
    title: "The Magic Chessboard",
    category: "Board Basics",
    description: "Learn about the board structure: 64 squares, ranks, files, and diagonals.",
    theory: "Welcome your kids to chess! Show them the chessboard is like a magical castle grid. Explain that columns going up-and-down are called 'Files' (labeled letters a to h) and rows going left-to-right are 'Ranks' (numbers 1 to 8). The diagonal lines are paths of the same color.",
    demoFen: "8/8/8/8/8/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Move the White King to a1! (A dark square in the corner)",
        fen: "8/8/8/8/8/8/8/4K3 w - - 0 1",
        solution: "Ke1f2"
      }
    ],
    minigame: { piece: "King", targets: ["a1"] }
  },
  {
    id: 2,
    title: "Chessboard Coordinates",
    category: "Board Basics",
    description: "Learn to name any square on the board like a treasure map code.",
    theory: "Teach kids that every square has a secret address! Combine the file letter first, and rank number second (e.g. e4, c6). Play 'Coordinates Bingo' by pointing to squares and asking them to shout the coordinate code.",
    demoFen: "8/8/8/8/4P3/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Load a pawn on e4!",
        fen: "8/8/8/8/8/8/4P3/8 w - - 0 1",
        solution: "e4"
      }
    ]
  },
  {
    id: 3,
    title: "The Initial Setup",
    category: "Board Basics",
    description: "Assemble the chess army in their starting castles.",
    theory: "Set up the pieces step-by-step: Rooks in the corners, Knights next to them, Bishops next to them, Queen on her matching color square (White Queen on white, Black Queen on black), King next to the Queen, and Pawns forming a protective wall in front.",
    demoFen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    puzzles: [
      {
        prompt: "Set up the white King on e1!",
        fen: "8/8/8/8/8/8/8/4K3 w - - 0 1",
        solution: "Ke1f1"
      }
    ]
  },
  {
    id: 4,
    title: "Army and Values",
    category: "Board Basics",
    description: "Understand the values of the pieces and the general goal.",
    theory: "Introduce piece weights: Pawn (1 point), Knight (3 points), Bishop (3 points), Rook (5 points), Queen (9 points). The King is priceless! The goal of chess is to trap the opponent's King in checkmate.",
    demoFen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    puzzles: []
  },
  {
    id: 5,
    title: "The Mighty Rook",
    category: "Piece Movements",
    description: "Learn how the Rook moves in straight vertical and horizontal lines.",
    theory: "Meet Rusty the Rook! The Rook is like a castle tower that moves in straight crosses (+): up, down, left, and right. It cannot jump over any pieces, but it captures by landing on the square of an enemy piece.",
    demoFen: "8/8/8/8/4R3/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Capture the black pawn with your Rook!",
        fen: "8/3p4/8/8/3R4/8/8/8 w - - 0 1",
        solution: "Rxd7"
      }
    ],
    minigame: { piece: "Rook", targets: ["d7"] }
  },
  {
    id: 6,
    title: "Rook Maze & Apple Hunt",
    category: "Piece Movements",
    description: "Practice sliding the Rook to capture target apples.",
    theory: "Time to feed our Rook! Guide the Rook around the board to capture apples. Remind kids to plan their route so they do not get blocked or waste moves.",
    demoFen: "8/8/8/8/8/8/8/8 w - - 0 1",
    puzzles: [],
    minigame: { piece: "Rook", targets: ["a8", "h8", "h1"] }
  },
  {
    id: 7,
    title: "The Silent Bishop",
    category: "Piece Movements",
    description: "Learn how the Bishop slides along diagonal lines.",
    theory: "Meet Benny the Bishop! The Bishop travels on diagonal 'X' paths. Because of this, it is color-bound: a light-squared Bishop stays on light squares forever, and a dark-squared Bishop stays on dark squares.",
    demoFen: "8/8/8/8/4B3/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Capture the black knight with your Bishop!",
        fen: "8/8/8/2n5/8/8/5B2/8 w - - 0 1",
        solution: "Bxc5"
      }
    ],
    minigame: { piece: "Bishop", targets: ["c5"] }
  },
  {
    id: 8,
    title: "Bishop Maze & Diagonals",
    category: "Piece Movements",
    description: "Practice sliding the Bishop diagonally to collect targets.",
    theory: "Bishop apple-hunt! Remember that the Bishop cannot jump over obstacles and must always slide diagonally.",
    demoFen: "8/8/8/8/8/8/8/8 w - - 0 1",
    puzzles: [],
    minigame: { piece: "Bishop", targets: ["a7", "g1", "b2"] }
  },
  {
    id: 9,
    title: "The Royal Queen",
    category: "Piece Movements",
    description: "Learn the power of the Queen, combining Rook and Bishop.",
    theory: "Meet the Queen! She is the superstar who can move straight like a Rook AND diagonally like a Bishop. She is the ultimate protector and attacker.",
    demoFen: "8/8/8/8/4Q3/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Capture the black rook with your Queen!",
        fen: "8/8/2r5/8/6Q1/8/8/8 w - - 0 1",
        solution: "Qxc8"
      }
    ],
    minigame: { piece: "Queen", targets: ["c8"] }
  },
  {
    id: 10,
    title: "Queen Maze & Target Practice",
    category: "Piece Movements",
    description: "Control the Queen to complete complex path puzzles.",
    theory: "Use the Queen's super movement combination to capture all target apples in as few moves as possible.",
    demoFen: "8/8/8/8/8/8/8/8 w - - 0 1",
    puzzles: [],
    minigame: { piece: "Queen", targets: ["a1", "h8", "a8", "h1"] }
  },
  {
    id: 11,
    title: "The Tricky Knight",
    category: "Piece Movements",
    description: "Learn the Knight's L-shaped jump over other pieces.",
    theory: "Meet jumpy Jack the Knight! The Knight moves in an 'L' shape: 2 squares forward/backward/left/right, then 1 square to the side. The Knight is special: it can jump over any friendly or enemy piece in its path!",
    demoFen: "8/8/8/8/4N3/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Capture the black bishop with your Knight!",
        fen: "8/8/8/8/8/2b5/4N3/8 w - - 0 1",
        solution: "Nxc3"
      }
    ],
    minigame: { piece: "Knight", targets: ["c3"] }
  },
  {
    id: 12,
    title: "Knight Maze & L-Shape Training",
    category: "Piece Movements",
    description: "Master the L-shape hops of the Knight.",
    theory: "Help the Knight jump and gather the target apples. Kids find this tricky at first, so encourage them to count 'one, two, turn!'",
    demoFen: "8/8/8/8/8/8/8/8 w - - 0 1",
    puzzles: [],
    minigame: { piece: "Knight", targets: ["f3", "g5", "e7"] }
  },
  {
    id: 13,
    title: "The Brave Pawn",
    category: "Piece Movements",
    description: "Learn pawn movement, initial double-step, and diagonal captures.",
    theory: "The Pawn is a small soldier. It moves 1 square straight forward, but on its very first move, it can choose to jump 2 squares. Pawns capture differently than they move: they capture 1 square diagonally forward!",
    demoFen: "8/8/8/8/8/8/4P3/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Push your pawn forward two squares!",
        fen: "8/8/8/8/8/8/4P3/8 w - - 0 1",
        solution: "e4"
      }
    ],
    minigame: { piece: "Pawn", targets: ["e4"] }
  },
  {
    id: 14,
    title: "Pawn Promotion & En Passant",
    category: "Piece Movements",
    description: "Learn the pawn's special abilities: promotion and en passant.",
    theory: "When a tiny pawn reaches the opposite end of the board, it gets promoted into a Queen, Rook, Bishop, or Knight! Also explain the special 'En Passant' (in-passing) capture when an enemy pawn jumps 2 squares next to yours.",
    demoFen: "8/3P4/8/8/8/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Promote your pawn to a Queen!",
        fen: "8/3P4/8/8/8/8/8/8 w - - 0 1",
        solution: "d8=Q"
      }
    ]
  },
  {
    id: 15,
    title: "The King & Castling",
    category: "Piece Movements",
    description: "Learn King movement and castling for safety.",
    theory: "The King is the chief! He can only step 1 square in any direction. Because he is slow, he has a special escape move called 'Castling' where the King moves 2 squares towards a Rook, and the Rook hops over him to safety.",
    demoFen: "8/8/8/8/8/8/8/4K2R w K - 0 1",
    puzzles: [
      {
        prompt: "Castle Kingside to protect your King!",
        fen: "8/8/8/8/8/8/8/4K2R w K - 0 1",
        solution: "O-O"
      }
    ]
  },
  {
    id: 16,
    title: "Castling Rules & Restrictions",
    category: "Piece Movements",
    description: "Understand when castling is not allowed.",
    theory: "Remember: You CANNOT castle if: 1. Your King or Rook has already moved. 2. There are pieces in between. 3. Your King is currently in check. 4. Your King has to pass through or land on a square attacked by an enemy piece.",
    demoFen: "8/8/8/8/8/3b4/8/4K2R w K - 0 1",
    puzzles: []
  },
  {
    id: 17,
    title: "Attacking Pieces",
    category: "Fundamental Concepts",
    description: "Recognize when a piece is threatened by an opponent.",
    theory: "An attack happens when your piece is in the line of movement of an opponent's piece. Help kids count how many pieces are attacking a target square and identify threats.",
    demoFen: "8/8/8/3r4/8/8/3B4/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Find the attacking move with your Bishop to target the Black Rook!",
        fen: "8/8/8/3r4/8/8/3B4/8 w - - 0 1",
        solution: "Bb4"
      }
    ]
  },
  {
    id: 18,
    title: "Capture & Counting Trades",
    category: "Fundamental Concepts",
    description: "Evaluate capturing trades using piece values.",
    theory: "Capturing means taking the opponent's piece off the board. Teach children to ask: 'Is this a good trade?' Don't trade a valuable Queen (9 pts) for a Knight (3 pts)!",
    demoFen: "8/8/8/3r4/8/3Q4/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Capture the black Rook with your Queen!",
        fen: "8/8/8/3r4/8/3Q4/8/8 w - - 0 1",
        solution: "Qxd5"
      }
    ]
  },
  {
    id: 19,
    title: "Defending: Running Away",
    category: "Fundamental Concepts",
    description: "Move an attacked piece to a safe square.",
    theory: "When your piece is under attack, one defense is 'Running Away'—moving your piece to a square where no enemy piece can capture it.",
    demoFen: "8/8/8/3r4/3B4/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Save your attacked Bishop by moving it to a safe square!",
        fen: "8/8/8/3r4/3B4/8/8/8 w - - 0 1",
        solution: "Bc3"
      }
    ]
  },
  {
    id: 20,
    title: "Defending: Blocking & Protecting",
    category: "Fundamental Concepts",
    description: "Protect pieces by blocking attacks or adding defenders.",
    theory: "If you cannot run away, you can: 1. Block the attack by putting another piece in front. 2. Protect/Guard the piece with another of your soldiers so the enemy won't capture without losing their own.",
    demoFen: "8/8/8/3r4/8/8/3B4/3K4 w - - 0 1",
    puzzles: [
      {
        prompt: "Protect your pinned Bishop by defending it with the King!",
        fen: "8/8/8/3r4/8/8/3B4/3K4 w - - 0 1",
        solution: "Ke2"
      }
    ]
  },
  {
    id: 21,
    title: "Safe vs Unsafe Squares",
    category: "Fundamental Concepts",
    description: "Identify zones where pieces can move without danger.",
    theory: "A square is 'safe' if no enemy piece is looking at it. Encourage kids to look at the entire board before moving to avoid stepping into 'unsafe hot zones'!",
    demoFen: "8/8/8/8/8/5r2/8/4B3 w - - 0 1",
    puzzles: [
      {
        prompt: "Move the Bishop to a safe square where the Rook cannot capture it!",
        fen: "8/8/8/8/8/5r2/8/4B3 w - - 0 1",
        solution: "Bf2"
      }
    ]
  },
  {
    id: 22,
    title: "What is Check?",
    category: "Checks and Escapes",
    description: "Learn what check is and how to identify it.",
    theory: "A 'Check' is an attack on the King! It's like an alarm bell ringing in the castle. The King must get out of check immediately—no other moves are allowed!",
    demoFen: "8/8/8/3r4/8/8/8/4K3 w - - 0 1",
    puzzles: [
      {
        prompt: "Deliver check to the Black King using your Queen!",
        fen: "8/8/3k4/8/8/8/8/3Q3K w - - 0 1",
        solution: "Qd4+"
      }
    ]
  },
  {
    id: 23,
    title: "Escape Check: C.B.R.",
    category: "Checks and Escapes",
    description: "Master the 3 escape rules: Capture, Block, Run away.",
    theory: "When in check, remember the magic formula C.B.R.: 1. Capture the attacking piece. 2. Block the attack by putting a piece in between. 3. Run away with the King to a safe square.",
    demoFen: "8/8/8/3r4/8/8/8/3BK3 w - - 0 1",
    puzzles: [
      {
        prompt: "Get out of check by Capturing (C) the attacking Rook with your Bishop!",
        fen: "8/8/8/3r4/8/8/8/3BK3 w - - 0 1",
        solution: "Bxd5"
      }
    ]
  },
  {
    id: 24,
    title: "Finding Checks",
    category: "Checks and Escapes",
    description: "Find moves that put the opponent's King in check.",
    theory: "Practice attacking the enemy King! Look for squares where your pieces can target the King while staying safe.",
    demoFen: "8/6k1/8/8/4R3/8/8/7K w - - 0 1",
    puzzles: [
      {
        prompt: "Find the rook move that puts the black King in check!",
        fen: "8/6k1/8/8/4R3/8/8/7K w - - 0 1",
        solution: "Re7+"
      }
    ]
  },
  {
    id: 25,
    title: "Double Check & Discovered Check",
    category: "Checks and Escapes",
    description: "Learn the most powerful tactical check threats.",
    theory: "A discovered check happens when one piece moves out of the way to open an attack from another piece. A double check is when two pieces attack the King at the same time! When in double check, the King MUST run away!",
    demoFen: "8/3k4/8/3r4/8/3B4/8/3R3K w - - 0 1",
    puzzles: []
  },
  {
    id: 26,
    title: "The Goal: Checkmate!",
    category: "Game Endings",
    description: "Understand the game-winning checkmate condition.",
    theory: "Checkmate is the end of the game! It means the King is in check and has absolutely NO way to escape (cannot Capture, Block, or Run away).",
    demoFen: "3k4/3Q4/3K4/8/8/8/8/8 b - - 0 1",
    puzzles: [
      {
        prompt: "Deliver checkmate in one move with your Queen!",
        fen: "3k4/8/3K4/8/8/8/8/3Q4 w - - 0 1",
        solution: "Qd7#"
      }
    ]
  },
  {
    id: 27,
    title: "The Ladder Mate",
    category: "Game Endings",
    description: "Learn to checkmate with two Rooks rolling like a ladder.",
    theory: "The Ladder Mate uses two Rooks to push the King to the edge of the board. One Rook guards the escape lane, while the other Rook checks the King, stepping over each other like climbing a ladder.",
    demoFen: "k7/8/1R6/2R5/8/8/8/7K w - - 0 1",
    puzzles: [
      {
        prompt: "Move the Rook to deliver checkmate on the edge!",
        fen: "k7/8/1R6/2R5/8/8/8/7K w - - 0 1",
        solution: "Ra5#"
      }
    ]
  },
  {
    id: 28,
    title: "Queen and King Mate",
    category: "Game Endings",
    description: "Learn how to wrap up a win with Queen + King vs King.",
    theory: "Use the Queen to trap the enemy King in a box, slowly shrinking it. Bring your own King to assist, then deliver checkmate once the enemy King is trapped on the edge.",
    demoFen: "k7/8/2K5/2Q5/8/8/8/8 w - - 0 1",
    puzzles: [
      {
        prompt: "Find the checkmate square for the Queen!",
        fen: "k7/8/2K5/2Q5/8/8/8/8 w - - 0 1",
        solution: "Qb6"
      }
    ]
  },
  {
    id: 29,
    title: "Scholar's Mate Trap",
    category: "Game Endings",
    description: "Learn the famous 4-move checkmate and how to defend it.",
    theory: "The Scholar's Mate targets the weak f7 pawn next to the King. Show how it works (1.e4 e5 2.Qh5 Nc6 3.Bc4 Nf6 4.Qxf7#) and teach students how to defend using pawn blocks or knight moves.",
    demoFen: "r1bqkbnr/pppp1ppp/2n5/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR b KQkq - 0 4",
    puzzles: [
      {
        prompt: "Defend against the Scholar's mate attack with your G pawn!",
        fen: "r1bqkbnr/pppp1ppp/2n5/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR b KQkq - 0 4",
        solution: "g6"
      }
    ]
  },
  {
    id: 30,
    title: "What is Stalemate?",
    category: "Game Endings",
    description: "Understand the draw condition when no legal moves exist.",
    theory: "Stalemate is a draw (tie)! It happens when a player is NOT in check, but has absolutely no legal moves left on their turn. Teach children to always check if the enemy King has escape squares before capturing everything!",
    demoFen: "k7/2Q5/2K5/8/8/8/8/8 b - - 0 1",
    puzzles: []
  },
  {
    id: 31,
    title: "Stalemate Traps",
    category: "Game Endings",
    description: "Learn how a losing player can force a stalemate draw.",
    theory: "A player who is losing can try to hide their King or trap their own pawns to force a stalemate. Always stay alert when you have a large lead!",
    demoFen: "k7/8/8/8/8/8/5q1p/7K w - - 0 1",
    puzzles: []
  },
  {
    id: 32,
    title: "Insufficient Material",
    category: "Game Endings",
    description: "Recognize when there aren't enough pieces to mate.",
    theory: "If neither side has enough pieces to force a mate, the game ends in an immediate draw. Insufficient material includes: King vs King, King + Bishop vs King, and King + Knight vs King.",
    demoFen: "8/8/8/8/3k4/8/3B4/3K4 w - - 0 1",
    puzzles: []
  },
  {
    id: 33,
    title: "Threefold Repetition & 50-Move Draw",
    category: "Game Endings",
    description: "Learn other draw rules like repeating positions.",
    theory: "Other draw rules: 1. Threefold Repetition: The exact same position appears 3 times on the board. 2. 50-Move Rule: No pawns have moved and no pieces have been captured for 50 moves.",
    demoFen: "8/8/8/8/8/8/8/k6K w - - 0 1",
    puzzles: []
  },
  {
    id: 34,
    title: "Basic Openings: Center Control",
    category: "Tactics & Strategy",
    description: "Learn the 3 golden opening principles.",
    theory: "The golden opening rules: 1. Control the center (e4/d4 squares). 2. Develop your Knights and Bishops. 3. Castle early to protect your King.",
    demoFen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    puzzles: [
      {
        prompt: "Open the game by pushing your King's pawn to control the center!",
        fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
        solution: "e4"
      }
    ]
  },
  {
    id: 35,
    title: "Tactics: The Fork",
    category: "Tactics & Strategy",
    description: "Learn to attack two pieces at the same time.",
    theory: "A fork is a double-attack! One of your pieces (often a Knight or Pawn) attacks two valuable enemy pieces at the same time. The opponent can only save one!",
    demoFen: "8/3k4/8/4N3/2r5/8/8/7K w - - 0 1",
    puzzles: [
      {
        prompt: "Fork the black King and Rook with your Knight!",
        fen: "8/3k4/8/8/2r5/3N4/8/7K w - - 0 1",
        solution: "Ne5+"
      }
    ]
  },
  {
    id: 36,
    title: "Tactics: Pins and Skewers",
    category: "Tactics & Strategy",
    description: "Learn to pin pieces or skewer them along lines.",
    theory: "A Pin is when an attacked piece cannot move because it would expose a more valuable piece behind it. A Skewer is the opposite: the valuable piece is in front, and when it runs away, you capture the piece behind it!",
    demoFen: "8/8/8/3r4/8/8/3B4/3K3q w - - 0 1",
    puzzles: []
  }
];

export default BASICS_CURRICULUM;